/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import React, { Component } from 'react';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import { SideNav, SideNavItem, SideNavHeading } from '@react/react-spectrum/SideNav';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger';
import Button from '@react/react-spectrum/Button';
import Popover from '@react/react-spectrum/Popover';
import ChevronDownMedium from '@react/react-spectrum/Icon/core/ChevronDownMedium';
import CheckmarkMedium from '@react/react-spectrum/Icon/core/CheckmarkMedium';
import StatusLight from '@react/react-spectrum/StatusLight';
import uuid from 'uuid';
import IconMapping from '../../utils/iconMapping';
import { sideNavAnalytics } from '../../analytics';
import './index.scss';
import CustomIcon from './customIcon/customIcon';
import { ANALYTICS } from '../../utils/enums';

// import { AppsPanelSkeleton } from '../../stores/skeleton';
// import Apps from '../../stores/apps';
let AppsPanelSkeleton;
let Apps;

class AppSideNav extends Component {
    @observable
    selectedItemId = null;

    @observable
    selectedItemName = null;

    componentWillMount() {
        const { apps, appsPanelSkeleton } = this.props;
        Apps = apps;
        AppsPanelSkeleton = appsPanelSkeleton;

        this.route(this.props);
    }

    componentDidMount() {
        this.isDefaultAnalyticsEventSent = false;
        this.sendDefaultLandedAnalyticEvent();
    }

    componentWillReceiveProps(nextProps) {
        const { location } = this.props;
        if (location && nextProps.location && location.pathname !== nextProps.location.pathname) {
            this.route(nextProps);
        }
    }

    componentDidUpdate() {
        if (!this.isDefaultAnalyticsEventSent) {
            this.sendDefaultLandedAnalyticEvent();
        }
    }

    onClick(appSection, event) {
        if (this.overlayRef) {
            this.overlayRef.hide();
        }
        if (appSection.className === 'ExternalLinkAppSection') {
            const guid = uuid.v4();
            sideNavAnalytics({
                subtype: appSection.ingestId ? appSection.ingestId : appSection.localizedText,
                sidenav: appSection.targetType !== 'in-app'
                    ? ANALYTICS.RESOURCES : undefined,
                link: appSection.url,
                guid,
            });
            if (!appSection.disabled) {
                appSection.setOnBoardingStatusLight(false);
                appSection.onSelect(appSection, event, { guid, url: appSection.url });
            }
        } else {
            AppsPanelSkeleton.setSelectedSectionFilter(appSection.id);
            sideNavAnalytics({
                subtype: appSection.id,
                count: appSection.count,
            });
            const { history, match } = this.props;
            history.push(`${match.path}/${appSection.id}`);
        }
    }

    getValidSideNavSection(section) {
        const { sectionGroupList = [] } = AppsPanelSkeleton;
        let sectionObj = null;
        if (sectionGroupList.length) {
            sectionGroupList.forEach(({ appSections }) => {
                appSections.forEach((sub) => {
                    if (sub.id === section) {
                        sectionObj = { badgeCount: sub.count };
                    }
                });
            });
        }
        return sectionObj;
    }

    sendDefaultLandedAnalyticEvent() {
        const { location } = this.props;
        if (location && location.pathname) {
            const [,, section] = location.pathname.split('/');
            const validSideSection = this.getValidSideNavSection(section);
            if (validSideSection) {
                sideNavAnalytics({
                    subtype: section,
                    value: 'default',
                    count: validSideSection.badgeCount,
                });
                this.isDefaultAnalyticsEventSent = true;
                AppsPanelSkeleton.setSelectedSectionFilter(section);
            }
        }
    }

    showOnBoardingStatusLight(appSections) {
        let showOnBoardingStatusLight = false;
        showOnBoardingStatusLight = appSections.some(
            ({ onBoardingStatusLight }) => onBoardingStatusLight
        );
        return showOnBoardingStatusLight;
    }

    route({ history, match, location }) {
        const { sectionGroupList = [] } = AppsPanelSkeleton;
        if (!match || !sectionGroupList.length) {
            return;
        }
        const { path: matchedPath } = match;
        const { pathname } = location;
        if (matchedPath === pathname || `${matchedPath}/` === pathname) {
            const { id } = sectionGroupList[0].appSections[0];
            history.replace(`${matchedPath}/${id}`);
            return;
        }
        sectionGroupList.forEach((navigationCategory) => {
            if (!navigationCategory.appSections) {
                return;
            }
            navigationCategory.appSections.forEach(({ id, localizedText }) => {
                if (pathname.startsWith(`${matchedPath}/${id}`)) {
                    this.updateSelectedItem(id, localizedText);
                }
            });
        });
    }

    @action
    updateSelectedItem(id, name) {
        this.selectedItemId = id;
        this.selectedItemName = name;
    }

    renderSidenav(className) {
        const { sectionGroupList } = AppsPanelSkeleton;
        const { LinkOut } = IconMapping;
        return (
            <SideNav key={className} value={this.selectedItemId} className={`app-sidenav ${className}`}>
                {
                    sectionGroupList.map(({
                        localizedText, appSections,
                    }) => {
                        const onBoardingStatusLight = this.showOnBoardingStatusLight(appSections);
                        return (
                            <SideNavHeading
                                key={localizedText}
                                label={(
                                    <div className="app-sidenav-header">
                                        <span>{localizedText}</span>
                                        {onBoardingStatusLight && <StatusLight variant="info" />}
                                    </div>
                                )}>
                                {
                                    appSections.reduce((items, appSection) => {
                                        const {
                                            id,
                                            icon,
                                            iconUrl,
                                            emptyState,
                                            count,
                                            disabled,
                                            targetType,
                                        } = appSection;
                                        // Since now the AppsPanelSkeleton object in layout is
                                        // received from outside layout JS as props, instanceof
                                        // cannot be used to identify its type, therefore have
                                        // injected className in the class itself and verifying
                                        // it here
                                        const isExternal = appSection.className === 'ExternalLinkAppSection';
                                        const filters = { appSectionFilter: id, isHidden: false };
                                        if (!isExternal && !emptyState
                                            && !Apps.existApps(filters)) {
                                            return items;
                                        }
                                        const Icon = (icon && IconMapping[icon])
                                            ? IconMapping[icon] : null;
                                        items.push((
                                            <SideNavItem
                                                className={isExternal ? 'sidenav-external-link-item' : ''}
                                                key={id}
                                                disabled={disabled}
                                                value={id}
                                                // eslint-disable-next-line no-nested-ternary
                                                icon={Icon ? <Icon /> : iconUrl
                                                    ? <CustomIcon imgSrc={iconUrl} /> : null}
                                                onClick={e => this.onClick(appSection, e)}
                                                label={(
                                                    <div className="app-sidenav-label">
                                                        <div className="app-sidenav-label-text">{appSection.localizedText}</div>
                                                        {!!count && <div className="app-sidenav-label-count">{count}</div>}
                                                        {this.selectedItemId === appSection.id && <CheckmarkMedium size={null} className="item-selected-icon" />}
                                                        {isExternal && targetType !== 'in-app' && <LinkOut size="XS" className="sidenav-label-linkout" />}
                                                    </div>
                                                )} />
                                        ));
                                        return items;
                                    }, [])
                                }
                            </SideNavHeading>
                        );
                    })
                }
            </SideNav>
        );
    }

    renderSidenavAsDropdown() {
        return (
            <OverlayTrigger key="appsidenav-dropdown" trigger="click" placement="bottom" ref={(r) => { this.overlayRef = r; }}>
                <Button
                    className="app-sidenav-sm-trigger"
                    type="button"
                    variant="field"
                    aria-haspopup="listbox"
                    quiet>
                    <span className="spectrum-Dropdown-label">
                        {this.selectedItemName}
                    </span>
                    <ChevronDownMedium size={null} className="spectrum-Dropdown-icon" />
                </Button>
                <Popover className="app-sidenav-popover" isDialog={false}>
                    {this.renderSidenav('app-sidenav-sm')}
                </Popover>
            </OverlayTrigger>
        );
    }

    render() {
        return (
            <div className="app-sidenav-container">
                {[
                    this.renderSidenavAsDropdown(),
                    this.renderSidenav('app-sidenav-large'),
                ]}
            </div>
        );
    }
}

export default observer(AppSideNav);
